import React from 'react';
import {useTranslation} from 'react-i18next';

import {TSelectOption} from '@edna/components/Select/types';
import {Icon, Stack} from '@edna/components/primitives';
import {TPlacementY} from '@edna/components/types';
import ENFlag from '@edna/country-flags/country-flags/GB.svg';
import RUFlag from '@edna/country-flags/country-flags/RU.svg';

import {ELanguage} from 'src/constants';

import * as S from './style';

const FLAG: Record<ELanguage, TSvgComponentType> = {
  ru: RUFlag,
  en: ENFlag,
};

const LABEL: Record<ELanguage, string> = {
  ru: 'Русский',
  en: 'English',
};

const options = Object.values(ELanguage).map((lang) => ({
  value: lang,
  label: LABEL[lang],
}));

type TProps = {
  className?: string;
  placementY?: TPlacementY;
  readOnly?: boolean;
  isDark?: boolean;
};

const Language = React.memo<TProps>(({className, placementY, readOnly, isDark}) => {
  const {i18n} = useTranslation();

  const renderLabel = React.useCallback(
    ({option}: {option?: TSelectOption<ELanguage>}) =>
      option?.value && (
        <Stack
          size="2"
          direction="ROW"
          align="center"
          key={option?.value}
          data-selector={`Language:select.${option.value}`}
        >
          <Icon as={FLAG[option.value]} size="24px" />
          {LABEL[option.value]}
        </Stack>
      ),
    [placementY],
  );

  if (isDark) {
    return (
      <S.DarkSelect
        readOnly={readOnly}
        className={className}
        placementY={placementY}
        renderLabel={renderLabel}
        options={options}
        optionComponent={S.DarkOption}
        value={i18n.language}
        onChange={i18n.changeLanguage}
        selector="Language:select"
      />
    );
  }

  return (
    <S.Select
      readOnly={readOnly}
      className={className}
      renderLabel={renderLabel}
      options={options}
      value={i18n.language}
      onChange={i18n.changeLanguage}
      selector="Language:select"
    />
  );
});

Language.displayName = 'Language';

export default Language;
